import type { SgDaterangepickerCustomEvent } from '@sg-bootstrap/components';
import { SgDaterangepicker } from '@sg-bootstrap/components/dist/react-library/src/components';
import type { DatePickerProps } from 'components/shared/DatePicker/DatePicker';
import { useDisablePresets, useTranslatePreset } from 'components/shared/DatePicker/hooks';
import { asIsoDate, fromTimestamp, toTimestamp } from 'components/shared/DatePicker/utils';
import { useLocalDateFormat } from 'hooks/useLocalDateFormat';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { now } from 'utils/core/now';

const StyledDaterangepicker = styled(SgDaterangepicker)`
  .dp__inputWrapper {
    min-width: 100% !important;
  }
`;
export type DatesRange = { startDate?: string; endDate?: string };
export type DateRangePickerProps = Omit<
  DatePickerProps,
  'values' | 'onKeyDown' | 'inputValue' | 'type' | 'onChange' | 'isFocused'
> & {
  startDate?: string | null;
  endDate?: string | null;
  onChange?: (dates: DatesRange) => void;
  defaultValues?: DatesRange;
  singleMonthCalendar?: boolean;
  isReadonly?: boolean;
  startPlaceholder?: string;
  endPlaceholder?: string;
  closeWhenClickOutside?: boolean;
};

const emptyArray: readonly [] = [];

export function DateRangePicker({
  className,
  id,
  e2e,
  size = 'md',
  align = 'start',
  allowWeekend = true,
  blackoutDates,
  isClearable,
  disabled,
  onChange,
  hasError,
  minDate,
  closeWhenClickOutside,
  maxDate,
  defaultValues,
  datePresetTranslations = emptyArray,
  datePresets = emptyArray,
  tabIndex,
  format,
  availableDates,
  hidePresets,
  startDate,
  endDate,
  singleMonthCalendar,
  isReadonly,
  startPlaceholder,
  endPlaceholder,
}: DateRangePickerProps) {
  const [dateFormat, locale] = useLocalDateFormat();
  const inputId = id + '-input';

  const onCalendarClosed = useCallback(
    ({ target }: SgDaterangepickerCustomEvent<void>) => {
      const data: DatesRange = { startDate: fromTimestamp(target.startDate), endDate: fromTimestamp(target.endDate) };
      if (data.startDate !== startDate || data.endDate !== endDate) {
        onChange?.(data);
      }
    },
    [startDate, endDate, onChange],
  );

  const onClearInput = useCallback(() => {
    if (defaultValues) {
      onChange?.(defaultValues);
    } else {
      onChange?.({});
    }
  }, [defaultValues, onChange]);

  const translatedPresets = useTranslatePreset(datePresetTranslations);

  const onPresetClicked = useCallback(
    ({ detail: { preset } }: CustomEvent<{ preset: string }>) => {
      const index = translatedPresets.indexOf(preset);
      if (index !== -1 && index < datePresets.length) {
        const calculation = datePresets[index];
        const endDate = asIsoDate(calculation(now()));
        const startDate = asIsoDate(now());
        onChange?.({ endDate, startDate });
      }
    },
    [translatedPresets, datePresets, onChange],
  );

  const disablePresets = useDisablePresets(allowWeekend, blackoutDates, minDate, maxDate);
  const availableTimestamps = useMemo(
    () => (availableDates === undefined ? undefined : availableDates.map(toTimestamp).join(',')),
    [availableDates],
  );
  const availabilityRule = availableTimestamps ?? disablePresets;

  const computedFormat = format ?? dateFormat;
  return (
    <StyledDaterangepicker
      key={`${inputId}-${availabilityRule}`}
      startDate={toTimestamp(startDate)}
      endDate={toTimestamp(endDate)}
      state={hasError ? 'warning' : undefined}
      disablePresets={availableTimestamps ? undefined : disablePresets}
      no-clear-button={isClearable === false}
      available-dates={availableTimestamps}
      classes={className ?? ''}
      input-id={inputId}
      data-e2e={e2e || id}
      size={size}
      singleMonthCalendar={singleMonthCalendar}
      onInputCleared={onClearInput}
      onCustomPresetClick={onPresetClicked}
      onCalendarClose={onCalendarClosed}
      never-empty={isClearable === false}
      disabled={disabled ?? false}
      startPlaceholder={startPlaceholder ?? computedFormat}
      endPlaceholder={endPlaceholder ?? computedFormat}
      format={computedFormat}
      display-format={format ?? undefined}
      locale={locale}
      readonly={isReadonly}
      custom-presets={hidePresets ? undefined : translatedPresets.join(',')}
      select-color="info"
      year-controllers
      tab-index={!disabled ? tabIndex : undefined}
      align-right={align === 'end'}
      close-when-click-outside={closeWhenClickOutside}
    />
  );
}
